import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface VehicleDocument {
  id: string;
  vehicle_id: string | null;
  company_id: string;
  document_type: 'registration_card' | 'insurance' | 'inspection' | 'other';
  file_name: string;
  file_url: string;
  file_size: number;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

interface UploadVehicleDocumentParams {
  file: File;
  vehicleId?: string;
  documentType?: 'registration_card' | 'insurance' | 'inspection' | 'other';
}

// Hook pour uploader une carte grise
export const useUploadVehicleDocument = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async ({ 
      file, 
      vehicleId, 
      documentType = 'registration_card' 
    }: UploadVehicleDocumentParams) => {
      // Get current user and company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.company_id) throw new Error('Company not found');

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `${userData.company_id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('vehicle-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('vehicle-documents')
        .getPublicUrl(filePath);

      // Insert metadata into vehicle_documents table
      const { data: documentData, error: documentError } = await supabase
        .from('vehicle_documents')
        .insert({
          vehicle_id: vehicleId || null,
          company_id: userData.company_id,
          document_type: documentType,
          file_name: file.name,
          file_url: publicUrl,
          file_size: file.size,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (documentError) throw documentError;

      return documentData.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-documents'] });
      toast({
        title: 'Document sauvegardé',
        description: 'La carte grise a été sauvegardée avec succès',
      });
    },
    onError: (error) => {
      console.error('Error uploading vehicle document:', error);
      toast({
        title: 'Erreur d\'upload',
        description: 'Impossible de sauvegarder le document',
        variant: 'destructive',
      });
    },
  });

  return {
    uploadDocument: uploadMutation.mutate,
    uploadDocumentAsync: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
  };
};

// Hook pour récupérer les documents d'un véhicule
export const useVehicleDocuments = (vehicleId?: string) => {
  const { data: documents, isLoading } = useQuery({
    queryKey: ['vehicle-documents', vehicleId],
    queryFn: async () => {
      let query = supabase
        .from('vehicle_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (vehicleId) {
        query = query.eq('vehicle_id', vehicleId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as VehicleDocument[];
    },
    enabled: !!vehicleId,
  });

  return { documents, isLoading };
};

// Hook pour supprimer un document
export const useDeleteVehicleDocument = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      // Get document info first
      const { data: doc, error: fetchError } = await supabase
        .from('vehicle_documents')
        .select('file_url')
        .eq('id', documentId)
        .single();

      if (fetchError) throw fetchError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('vehicle_documents')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;

      // Delete from storage
      if (doc?.file_url) {
        const filePath = doc.file_url.split('/vehicle-documents/')[1];
        if (filePath) {
          await supabase.storage.from('vehicle-documents').remove([filePath]);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-documents'] });
      toast({
        title: 'Document supprimé',
        description: 'Le document a été supprimé avec succès',
      });
    },
    onError: (error) => {
      console.error('Error deleting document:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le document',
        variant: 'destructive',
      });
    },
  });

  return {
    deleteDocument: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
